import _ from 'underscore';

import {defensiveGetFromState, defensiveGetState, NotYetFetchedException} from 'connect/DefensiveGet';
import {DataTable, DataTableColumn} from 'notebook/data-tables/DataTableConstants';
import {convertDataTableToTable} from 'notebook/data-tables/DataTableUtil';
import {Entry} from 'notebook/entries/backbone/filestate/Entry';
import getFormattedCellValue, {
  FormattedCellValue,
  TOTAL_LOCK_CELL_STATE,
} from 'notebook/structured-tables/columns/FormattedCellValue';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {ColumnConstraint} from 'notebook/tables/TableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BenchlingState} from 'redux-stores/reduceApp';
import {AssayResult, DATA_MISSING} from 'results/assays/AssayRecordConstants';
import {getTagForField} from 'results/assays/getTagForField';
import {getPrimaryLinkField, getPrimaryLinkTagForResult} from 'results/PrimaryLinks';
import {AssayResultIdsByRunId, AssayResultsById, filterOutDataMissing} from 'results/reduceResults';
import {getAllDataTables} from 'results/ResultsEntryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as logging from 'util/Logging';

import {AssayResultValidation} from '../ValidationConstants';
import {getLastAssayValidationChangeForField} from '../ValidationSelectors';
import {ResultStatusTabRowShape, ResultStatusTabTableShape} from './ResultStatusTabConstants';

export function getResultStatusTabTables(
  entryFile: Entry,
  relevantStateObjects: RelevantStateObjects
): Array<ResultStatusTabTableShape> {
  const {assayResultsById, assayValidationById, assayResultIdsByRunId} = relevantStateObjects;
  const dataTablesInEntry: Array<DataTable> = getAllDataTables(
    entryFile.getEntryNote(),
    // eslint-disable-next-line no-restricted-syntax
    _.partial(getResultsForDataTableInRun, assayResultsById, assayResultIdsByRunId)
  );
  return _.compact(
    dataTablesInEntry.map((dataTable) =>
      adaptDataTableToResultStatusTable(dataTable, assayResultsById, assayValidationById)
    )
  );
}

function getResultsForDataTableInRun(
  assayResultsById: AssayResultsById,
  assayResultIdsByRunId: AssayResultIdsByRunId,
  dataTable: DataTable,
  runId: string
): Array<AssayResult> {
  try {
    const resultIds = defensiveGetFromState(assayResultIdsByRunId, runId);
    if (resultIds === DATA_MISSING) {
      return [];
    }
    return filterOutDataMissing(resultIds.map((resultId) => assayResultsById[resultId])).filter(
      (result) => result.schemaId === dataTable.config.assaySchema.id
    );
  } catch (e) {
    if (e instanceof NotYetFetchedException) {
      return [];
    } else {
      throw e;
    }
  }
}

function adaptDataTableToResultStatusTable(
  dataTable: DataTable,
  assayResultsById: AssayResultsById,
  assayValidationById: {[resultId: string]: AssayResultValidation}
): ResultStatusTabTableShape | null {
  const primaryLinkField = getPrimaryLinkField(dataTable.config.assaySchema);
  const nonPrimaryLinkFields = dataTable.config.assaySchema.allFields.filter((field) =>
    primaryLinkField ? field.id !== primaryLinkField.id : true
  );
  const fieldsByDbId = _.indexBy(nonPrimaryLinkFields, 'id');
  const columnConstraints = convertDataTableToTable(dataTable).columnConstraints;

  const resultStatusTabRows = dataTable.rows.flatMap(({euid, cells}) => {
    let result: AssayResult;
    let resultValidation: AssayResultValidation;
    try {
      const maybeResult = defensiveGetFromState(assayResultsById, euid);
      if (maybeResult === DATA_MISSING) {
        return [];
      }
      result = maybeResult;
      resultValidation = defensiveGetFromState(assayValidationById, euid);
    } catch (e) {
      if (e instanceof NotYetFetchedException) {
        // If the `euid` for this row is not yet present in 1 of the state objects, just skip it for now --
        // the redux callback in the connector will trigger a re-compute as soon as it has been fetched
        return [];
      } else {
        throw e;
      }
    }
    return _.compact(
      cells.map((cell, index) =>
        dataTableCellToStatusTabRow(
          cell,
          dataTable.config.columns[index],
          columnConstraints[index],
          fieldsByDbId,
          result,
          resultValidation,
          primaryLinkField
        )
      )
    );
  });
  return resultStatusTabRows.length > 0
    ? {tableName: dataTable.config.name, rows: resultStatusTabRows}
    : null;
}

function dataTableCellToStatusTabRow(
  cell: Cell,
  column: DataTableColumn,
  columnConstraint: ColumnConstraint,
  fieldsByDbId: {[id: number]: SchemaField},
  result: AssayResult,
  resultValidation: AssayResultValidation,
  primaryLinkField: SchemaField | undefined
): ResultStatusTabRowShape | null {
  const schemaFieldDbId = column.tagSchemaFieldId;
  if (!schemaFieldDbId) {
    // Indicates this cell is in a custom column (not part of the result schema)
    return null;
  }
  const fieldForCell = fieldsByDbId[schemaFieldDbId];
  if (!fieldForCell) {
    logging.warn(
      "Could not find schema field corresponding to column's field ID when getting assay validation. " +
        "This almost always means the result schema's data table config has a column corresponding to " +
        'the primary link field. These data table configs should be cleaned up. (BNCH-14479)'
    );
    return null;
  }
  const assayValidationForCell = getLastAssayValidationChangeForField(resultValidation, fieldForCell.apiId);
  if (assayValidationForCell) {
    return {
      resultId: result.id,
      primaryLinkFieldTag: getPrimaryLinkTagForResult(primaryLinkField, result),
      fieldName: fieldForCell.displayName,
      formattedValue: formattedValueForCell(cell, columnConstraint, result, schemaFieldDbId),
      lastAssayValidationChange: assayValidationForCell,
    };
  }
  return null;
}

function formattedValueForCell(
  cell: Cell,
  columnConstraint: ColumnConstraint,
  result: AssayResult,
  schemaFieldDbId: number
): FormattedCellValue {
  const tagForCell = getTagForField(result, schemaFieldDbId);
  const typedValue = tagForCell ? convertTagToTypedValue(tagForCell) : null;
  return getFormattedCellValue(
    columnConstraint,
    cell,
    typedValue,
    TOTAL_LOCK_CELL_STATE,
    // DataTables don't have rowMetadata.
    null
  );
}

export interface RelevantStateObjects {
  assayResultsById: AssayResultsById;
  assayValidationById: {[resultId: string]: AssayResultValidation};
  assayResultIdsByRunId: AssayResultIdsByRunId;
}

export function getRelevantStateObjects(reduxState: BenchlingState): RelevantStateObjects {
  return {
    assayResultsById: defensiveGetState(reduxState, (state) => state.models.results.assayResultsById),
    assayValidationById: defensiveGetState<{[resultId: string]: AssayResultValidation}>(
      reduxState,
      (state) => state.models.managed.assayResultValidation!
    ),
    // `assayResultIdsByRunId` isn't always required, so don't use `defensiveGetState` here and let
    // `defensiveGetFromState` throw the exception later on if we do in fact have runs in the entry
    assayResultIdsByRunId: reduxState.models.results.assayResultIdsByRunId,
  };
}
